import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Plus, Search, Tags, ToggleLeft, ToggleRight } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { DataTablePagination } from '@/components/common/data-table-pagination'
import { getErrorMessage } from '@/lib/api'
import { useStoreOrgScope } from '@/hooks/use-store-org-scope'
import storeCommerceApi from '@/services/store-commerce'
import { STORE_TAG_ITEM_TYPES, type StoreTag, type StoreTaggedItemPayload, type StoreTagItemType, type StoreTagset } from '@/types'

type TargetType = 'product' | 'product_variant' | 'category' | 'master_category' | 'offer' | 'store'

type Props = {
  targetType: TargetType
}

const targetConfig: Record<TargetType, { title: string; itemType: StoreTagItemType; param: keyof StoreTaggedItemPayload; backPath: string }> = {
  product: { title: 'Product Tags', itemType: STORE_TAG_ITEM_TYPES.product, param: 'product', backPath: '/services/products' },
  product_variant: { title: 'Product Variant Tags', itemType: STORE_TAG_ITEM_TYPES.productVariant, param: 'product_variant', backPath: '/services/product-variants' },
  category: { title: 'Category Tags', itemType: STORE_TAG_ITEM_TYPES.category, param: 'category', backPath: '/services/categories' },
  master_category: { title: 'Master Category Tags', itemType: STORE_TAG_ITEM_TYPES.category, param: 'category', backPath: '/services/categories?view=master' },
  offer: { title: 'Offer Tags', itemType: STORE_TAG_ITEM_TYPES.offer, param: 'offer', backPath: '/services/offers' },
  store: { title: 'Store Tags', itemType: STORE_TAG_ITEM_TYPES.store, param: 'node', backPath: '/org/organization' },
}

const isActive = (value?: number | boolean | null) => !(value === false || value === 0)

const getStatusBadgeClassName = (active: boolean) => active
  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
  : 'border-slate-200 bg-slate-50 text-slate-700'

const getToggleButtonClassName = (active: boolean) => active
  ? 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100 hover:text-red-800'
  : 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:text-emerald-800'

export default function CommerceItemTagsPage({ targetType }: Props) {
  const params = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { orgInstanceId, serviceInstanceId } = useStoreOrgScope()
  const config = targetConfig[targetType]
  const targetId = params.productId ?? params.variantId ?? params.categoryId ?? params.masterCategoryId ?? params.offerId ?? params.storeId ?? ''
  const [search, setSearch] = useState('')
  const [activeFilter, setActiveFilter] = useState('-1')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedTagsetId, setSelectedTagsetId] = useState('')
  const [selectedTagId, setSelectedTagId] = useState('')
  const [sortingOrder, setSortingOrder] = useState('1')

  const taggedItemParams = useMemo(() => {
    const masterScopeServiceInstanceId = targetType === 'master_category' ? (orgInstanceId ?? serviceInstanceId ?? undefined) : undefined
    const queryParams: Record<string, string | number | undefined> = {
      active: activeFilter,
      item_type: config.itemType,
      service_instance_id: masterScopeServiceInstanceId,
      org_instance: targetType === 'store' ? orgInstanceId ?? undefined : undefined,
      store: targetType === 'store' ? targetId : undefined,
      product: targetType === 'product' ? targetId : undefined,
      product_variant: targetType === 'product_variant' ? targetId : undefined,
      category: targetType === 'category' || targetType === 'master_category' ? targetId : undefined,
      offer: targetType === 'offer' ? targetId : undefined,
    }
    return queryParams
  }, [activeFilter, config.itemType, orgInstanceId, serviceInstanceId, targetId, targetType])

  const taggedItemsQuery = useQuery({
    queryKey: ['store-tagged-items', targetType, targetId, orgInstanceId, activeFilter],
    queryFn: () => storeCommerceApi.listTaggedItems({ page: 1, page_size: 500, ...taggedItemParams }),
    enabled: Boolean(targetId) && (targetType !== 'store' || Boolean(orgInstanceId)),
    staleTime: 30_000,
  })

  const tagsetsQuery = useQuery({
    queryKey: ['store-tagsets-for-assignment', serviceInstanceId, orgInstanceId, config.itemType, targetType],
    queryFn: () => storeCommerceApi.listTagsetsForItemType(config.itemType),
    enabled: Boolean(serviceInstanceId),
    staleTime: 30_000,
  })

  const tagsQuery = useQuery({
    queryKey: ['store-tags-for-assignment', selectedTagsetId, config.itemType],
    queryFn: () => storeCommerceApi.listTagsForItemType(selectedTagsetId, config.itemType),
    enabled: Boolean(selectedTagsetId),
    staleTime: 30_000,
  })

  const taggedItems = taggedItemsQuery.data?.results ?? []
  const tagsetOptions = tagsetsQuery.data?.results ?? []
  const tagOptions = tagsQuery.data?.results ?? []
  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return taggedItems
    return taggedItems.filter((item) => [item.title, item.code, item.tagset_name].some((value) => String(value ?? '').toLowerCase().includes(term)))
  }, [search, taggedItems])

  const pageRows = useMemo(() => {
    const start = (page - 1) * pageSize
    return filteredItems.slice(start, start + pageSize)
  }, [filteredItems, page, pageSize])

  const invalidateTaggedItems = () => queryClient.invalidateQueries({ queryKey: ['store-tagged-items'] })

  const assignMutation = useMutation({
    mutationFn: (payload: StoreTaggedItemPayload) => {
      if (targetType === 'product') return storeCommerceApi.tagProduct(payload)
      if (targetType === 'product_variant') return storeCommerceApi.tagProductVariant(payload)
      if (targetType === 'category') return storeCommerceApi.tagCategory(payload)
      if (targetType === 'master_category') {
        return storeCommerceApi.tagCategory(payload, {
          service_instance_id: orgInstanceId ?? serviceInstanceId ?? undefined,
        })
      }
      if (targetType === 'offer') return storeCommerceApi.tagOffer(payload)
      return storeCommerceApi.tagStore(payload)
    },
    onSuccess: () => {
      invalidateTaggedItems()
      toast.success('Tag assigned')
      setDialogOpen(false)
      setSelectedTagsetId('')
      setSelectedTagId('')
      setSortingOrder('1')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const statusMutation = useMutation({
    mutationFn: ({ id, active }: { id: string | number; active: number }) => storeCommerceApi.updateTaggedItemStatus(id, { active }),
    onSuccess: () => {
      invalidateTaggedItems()
      toast.success('Tag status updated')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const submitAssignment = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!selectedTagId || !targetId) {
      toast.error('Select a tag')
      return
    }

    const payload: StoreTaggedItemPayload = {
      tag: selectedTagId,
      sorting_order: sortingOrder || '1',
      org_instance: targetType === 'store' ? orgInstanceId ?? undefined : undefined,
    }
    payload[config.param] = targetId
    assignMutation.mutate(payload)
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="border-b border-border/60">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <Button type="button" variant="ghost" size="icon" onClick={() => navigate(config.backPath)}><ArrowLeft className="h-4 w-4" /></Button>
              <div>
                <CardTitle>{config.title}</CardTitle>
              </div>
            </div>
            <Button onClick={() => { void tagsetsQuery.refetch(); setDialogOpen(true) }}><Plus className="mr-2 h-4 w-4" />Assign Tag</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 p-4 sm:p-6">
          <div className="grid gap-3 lg:grid-cols-[1fr_180px]">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input className="pl-9" placeholder="Search assigned tags" value={search} onChange={(event) => { setSearch(event.target.value); setPage(1) }} />
            </div>
            <Select value={activeFilter} onValueChange={(value) => { setActiveFilter(value); setPage(1) }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="-1">View all</SelectItem>
                <SelectItem value="1">Active</SelectItem>
                <SelectItem value="0">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tag</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Tagset</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-24">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {taggedItemsQuery.isLoading ? (
                  <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">Loading tags...</TableCell></TableRow>
                ) : taggedItemsQuery.isError ? (
                  <TableRow><TableCell colSpan={5} className="py-10 text-center text-destructive">{getErrorMessage(taggedItemsQuery.error)}</TableCell></TableRow>
                ) : pageRows.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">No tags assigned.</TableCell></TableRow>
                ) : pageRows.map((item) => (
                  <TableRow key={String(item.id)}>
                    <TableCell className="font-medium"><div className="flex items-center gap-2"><Tags className="h-4 w-4 text-muted-foreground" />{item.title ?? '-'}</div></TableCell>
                    <TableCell>{item.code ?? '-'}</TableCell>
                    <TableCell>{item.tagset_name ?? '-'}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={getStatusBadgeClassName(isActive(item.active))}>{isActive(item.active) ? 'Active' : 'Inactive'}</Badge>
                    </TableCell>
                    <TableCell>
                      <Button type="button" variant="outline" size="sm" className={getToggleButtonClassName(isActive(item.active))} disabled={statusMutation.isPending} onClick={() => statusMutation.mutate({ id: item.id, active: isActive(item.active) ? 0 : 1 })}>
                        {isActive(item.active) ? <ToggleRight className="mr-2 h-4 w-4" /> : <ToggleLeft className="mr-2 h-4 w-4" />}
                        {isActive(item.active) ? 'Disable' : 'Enable'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <DataTablePagination page={page} pageSize={pageSize} totalCount={filteredItems.length} onPageChange={setPage} onPageSizeChange={(value) => { setPageSize(value); setPage(1) }} />
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <form onSubmit={submitAssignment} className="space-y-4">
            <DialogHeader><DialogTitle>Assign Tag</DialogTitle></DialogHeader>
            <div className="space-y-2">
              <Label>Tagset</Label>
              <Select value={selectedTagsetId} onValueChange={(value) => { setSelectedTagsetId(value); setSelectedTagId('') }}>
                <SelectTrigger className="w-full"><SelectValue placeholder="Select tagset" /></SelectTrigger>
                <SelectContent position="popper" className="z-[200]">
                  {tagsetsQuery.isLoading ? (
                    <SelectItem value="__loading" disabled>Loading tagsets...</SelectItem>
                  ) : tagsetsQuery.isError ? (
                    <SelectItem value="__error" disabled>{getErrorMessage(tagsetsQuery.error)}</SelectItem>
                  ) : tagsetOptions.length === 0 ? (
                    <SelectItem value="__empty" disabled>No tagsets available</SelectItem>
                  ) : tagsetOptions.map((tagset: StoreTagset) => (
                    <SelectItem key={String(tagset.id)} value={String(tagset.id)}>
                      {tagset.name || tagset.code || 'Untitled tagset'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tag</Label>
              <Select value={selectedTagId} onValueChange={setSelectedTagId} disabled={!selectedTagsetId}>
                <SelectTrigger className="w-full"><SelectValue placeholder="Select tag" /></SelectTrigger>
                <SelectContent position="popper" className="z-[200]">
                  {!selectedTagsetId ? (
                    <SelectItem value="__pick-tagset" disabled>Select a tagset first</SelectItem>
                  ) : tagsQuery.isLoading ? (
                    <SelectItem value="__loading-tags" disabled>Loading tags...</SelectItem>
                  ) : tagsQuery.isError ? (
                    <SelectItem value="__error-tags" disabled>{getErrorMessage(tagsQuery.error)}</SelectItem>
                  ) : tagOptions.length === 0 ? (
                    <SelectItem value="__empty-tags" disabled>No tags available</SelectItem>
                  ) : tagOptions.map((tag: StoreTag) => (
                    <SelectItem key={String(tag.id)} value={String(tag.id)}>
                      {tag.title || tag.code || 'Untitled tag'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Sort Order</Label>
              <Input type="number" min="0" value={sortingOrder} onChange={(event) => setSortingOrder(event.target.value)} />
            </div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button><Button type="submit" disabled={assignMutation.isPending || !selectedTagId}>Assign</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
